import { sortBy } from "~/utils";

export type Podium<T> = {
  index: 1 | 2 | 3;
  group: T[];
}[];

const isPodiumIndex = (index: number): index is Podium<{}>[0]["index"] => {
  return [1, 2, 3].indexOf(index) !== -1;
};

export type PlayerWithStrength<T> = {
  player: T;
  strength: number;
};

export const Podium = {
  fromPlayers<T>(playersWithStrengths: PlayerWithStrength<T>[]): Podium<T> {
    const gameSize = playersWithStrengths.length;
    playersWithStrengths = playersWithStrengths.filter(
      ({ strength }) => strength
    );
    const winnerGroups = playersWithStrengths.reduce<PlayerWithStrength<T>[][]>(
      (acc, playerWithStrength) => {
        const { strength } = playerWithStrength;
        const samePointsIndex = acc.findIndex(
          (group) => strength === group[0].strength
        );
        if (samePointsIndex !== -1) {
          acc[samePointsIndex].push(playerWithStrength);
          return acc;
        }
        const lowerGroupIndex = acc.findIndex(
          (group) => strength > group[0].strength
        );
        if (lowerGroupIndex !== -1) {
          return [
            ...acc.slice(0, lowerGroupIndex),
            [playerWithStrength],
            ...acc.slice(lowerGroupIndex),
          ];
        } else {
          acc.push([playerWithStrength]);
          return acc;
        }
      },
      []
    );
    const { podium } = winnerGroups.reduce<{
      currentIndex: number;
      podium: Podium<T>;
    }>(
      (acc, group) => {
        const { podium } = acc;
        acc.currentIndex += 1;
        const index = acc.currentIndex;
        if (isPodiumIndex(index)) {
          podium.push({
            index,
            group: group.map(({ player }) => player),
          });
        }
        return acc;
      },
      { currentIndex: 0, podium: [] }
    );
    let sortedPodium = sortBy(podium, ({ index }) => index);
    const [firstPlace, secondPlace, thirPlace] = sortedPodium;
    if (firstPlace.group.length > 1) {
      firstPlace.index = 2;
      if (
        gameSize === 4 &&
        firstPlace.group.length === 2 &&
        secondPlace?.group.length === 1
      ) {
        secondPlace.index = 3;
        sortedPodium = [firstPlace, secondPlace];
      } else {
        sortedPodium = [firstPlace];
      }
      return sortedPodium;
    }
    if (secondPlace && secondPlace.group.length > 1) {
      secondPlace.index = 3;
      return [firstPlace, secondPlace];
    }
    if (thirPlace && thirPlace.group.length > 1) {
      return [firstPlace, secondPlace];
    }
    if (gameSize === 3) {
      sortedPodium = sortedPodium.slice(0, 2);
    }
    return sortedPodium;
  },
};
